import { useEffect, useState } from "react";
import { API, authFetch, useAuth } from "@/App";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Calendar, Edit2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_OPTIONS = [
  { value: "PRESENT", label: "Present", short: "PRE", color: "#22C55E" },
  { value: "ABSENT", label: "Absent", short: "ABS", color: "#EF4444" },
  { value: "HALF_DAY", label: "Half Day", short: "HD", color: "#EAB308" },
  { value: "CASUAL", label: "Casual Leave", short: "CL", color: "#A855F7" },
  { value: "SICK", label: "Sick Leave", short: "SL", color: "#F97316" },
  { value: "PAID", label: "Paid Leave", short: "PL", color: "#3B82F6" },
  { value: "UNPAID", label: "Unpaid Leave", short: "UL", color: "#6B7280" },
  { value: "EMERGENCY", label: "Emergency Leave", short: "EM", color: "#DC2626" },
  { value: "WEEKLY_OFF", label: "Weekly Off", short: "WO", color: "#94A3B8" },
  { value: "FUTURE", label: "Upcoming", short: "—", color: "#E2E8F0" },
];

const STATUS_BY_VALUE = STATUS_OPTIONS.reduce((acc, s) => { acc[s.value] = s; return acc; }, {});

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const AttendanceCalendar = ({ employees }) => {
  const { user } = useAuth();
  const canOverride = ["SUPER_ADMIN", "FACTORY_ADMIN", "SALES_ADMIN"].includes(user?.role);
  const today = new Date();
  const [employeeId, setEmployeeId] = useState("");
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState({ days: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [editDay, setEditDay] = useState(null);
  const [editStatus, setEditStatus] = useState("PRESENT");
  const [saving, setSaving] = useState(false);
  const [highlightStatus, setHighlightStatus] = useState(null); // click-to-filter legend chip

  useEffect(() => {
    if (employees?.length && !employeeId) setEmployeeId(employees[0].employee_id);
  }, [employees, employeeId]);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`${API}/attendance/calendar/${employeeId}?year=${year}&month=${month}`);
        const d = await res.json();
        if (d.success) setData(d.data);
      } catch {}
      finally { setLoading(false); }
    })();
  }, [employeeId, year, month]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  const saveOverride = async () => {
    if (!editDay) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API}/attendance/manual-update`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employee_id: employeeId, date: editDay.date, status: editStatus }),
      });
      const d = await res.json();
      if (d.success) {
        toast.success(d.message || "Updated");
        setEditDay(null);
        // Refetch
        const r = await authFetch(`${API}/attendance/calendar/${employeeId}?year=${year}&month=${month}`);
        const dd = await r.json();
        if (dd.success) setData(dd.data);
      } else toast.error(d.detail || "Failed");
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  const monthName = new Date(year, month - 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
  // Pad start (Monday=0). Backend weekday=monday=0... uses calendar.weekday where Mon=0.
  const firstWeekday = data.days?.[0]?.date ? (new Date(data.days[0].date).getDay() + 6) % 7 : 0;

  return (
    <Card className="p-5" data-testid="attendance-calendar">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#8B1A1A]" />
          <h3 className="font-bold text-slate-900 text-lg">Monthly Attendance Calendar</h3>
        </div>
        <div className="flex items-center gap-2">
          <Select value={employeeId} onValueChange={setEmployeeId}>
            <SelectTrigger className="w-56" data-testid="calendar-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              {(employees || []).map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.full_name} ({e.employee_code})</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={prevMonth} data-testid="prev-month"><ChevronLeft className="w-4 h-4" /></Button>
          <div className="font-semibold text-slate-800 min-w-[150px] text-center" data-testid="current-month">{monthName}</div>
          <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={nextMonth} data-testid="next-month"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Legend (clickable to highlight a status) */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs" data-testid="attendance-legend">
        {STATUS_OPTIONS.filter(s => s.value !== "FUTURE").map(s => {
          const active = highlightStatus === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setHighlightStatus(active ? null : s.value)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-full border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
              data-testid={`legend-${s.value.toLowerCase()}`}
            >
              <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} />
              <span>{s.label}</span>
            </button>
          );
        })}
        {highlightStatus && (
          <button type="button" onClick={() => setHighlightStatus(null)} className="text-[10px] underline text-slate-500 hover:text-slate-700">clear filter</button>
        )}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="py-12 text-center"><div className="w-6 h-6 spinner mx-auto" /></div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map(w => <div key={w} className="text-xs font-semibold text-slate-500 text-center py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstWeekday }).map((_, i) => <div key={`pad-${i}`} />)}
            {(data.days || []).map(day => {
              const dimmed = highlightStatus && day.status !== highlightStatus;
              const cfg = STATUS_BY_VALUE[day.status] || { short: "—", color: day.color || "#E2E8F0" };
              return (
              <button
                key={day.date}
                type="button"
                onClick={() => { if (canOverride) { setEditDay(day); setEditStatus(day.status === "FUTURE" ? "PRESENT" : day.status); } }}
                disabled={!canOverride}
                className={`relative aspect-square rounded-md p-1.5 text-left transition ${dimmed ? "opacity-25" : "hover:opacity-90"} ${canOverride ? "cursor-pointer" : "cursor-default"}`}
                style={{ backgroundColor: (cfg.color || "#E2E8F0") + "33", borderLeft: `3px solid ${cfg.color || "#E2E8F0"}` }}
                data-testid={`cal-day-${day.day}`}
                title={`${day.date} — ${day.status}`}
              >
                <div className="flex items-start justify-between">
                  <span className="text-xs font-bold text-slate-800">{day.day}</span>
                  {canOverride && <Edit2 className="w-2.5 h-2.5 text-slate-400" />}
                </div>
                {/* Prominent status chip */}
                {day.status && day.status !== "FUTURE" && (
                  <div
                    className="inline-block mt-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white tracking-wide"
                    style={{ backgroundColor: cfg.color }}
                    data-testid={`day-tag-${day.day}`}
                  >
                    {cfg.short}
                  </div>
                )}
                {day.hours > 0 && <div className="text-[9px] text-slate-500 mt-0.5">{day.hours}h</div>}
              </button>
              );
            })}
          </div>
        </>
      )}

      {/* Summary */}
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-5 border-t pt-4">
        {[
          ["Present", data.summary.present, "#22C55E"],
          ["Absent", data.summary.absent, "#EF4444"],
          ["Half Day", data.summary.half_day, "#EAB308"],
          ["Casual", data.summary.casual_leave, "#A855F7"],
          ["Sick", data.summary.sick_leave, "#F97316"],
          ["Paid", data.summary.paid_leave, "#3B82F6"],
          ["Emergency", data.summary.emergency, "#DC2626"],
          ["Week Off", data.summary.weekly_off, "#94A3B8"],
        ].map(([label, val, c]) => (
          <div key={label} className="text-center" data-testid={`summary-${label.toLowerCase().replace(" ", "-")}`}>
            <div className="text-lg font-bold font-mono" style={{ color: c }}>{val || 0}</div>
            <div className="text-[10px] text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Override Dialog */}
      <Dialog open={!!editDay} onOpenChange={(o) => { if (!o) setEditDay(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="override-dialog">
          <DialogHeader>
            <DialogTitle>Manual Attendance Override</DialogTitle>
          </DialogHeader>
          {editDay && (
            <div className="space-y-3 py-2">
              <p className="text-sm text-slate-600">Override attendance for <strong>{editDay.date}</strong> ({editDay.weekday})</p>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="override-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: s.color }} /> {s.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDay(null)}>Cancel</Button>
                <Button className="btn-primary" onClick={saveOverride} disabled={saving} data-testid="override-save-btn">
                  {saving ? "Saving..." : "Save Override"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AttendanceCalendar;
